import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, CheckCircle2, AlertCircle, AlertTriangle, ExternalLink,
  ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { reportApi } from '@/api/report.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { SavedViewBar } from '@/components/reports/SavedViewBar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { FinancialPeriodSelect } from '@/components/reports/FinancialPeriodSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import type {
  TrialBalanceDto, TrialBalanceFilter, TrialBalanceMode, TrialBalanceRow,
} from '@/types/report.types';

// ── helpers ────────────────────────────────────────────────────────────────────

function today() {
  return new Date().toISOString().split('T')[0];
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function fmtAmt(n: number | null) {
  if (n === null || n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtPct(n: number | null) {
  if (n === null) return '—';
  return `${n.toFixed(1)}%`;
}
function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso ?? '—';
  }
}
function sumCells(r: TrialBalanceRow): number {
  return (r.cells ?? []).reduce((a, b) => a + b, 0);
}

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'Asset', label: 'Asset' },
  { value: 'Liability', label: 'Liability' },
  { value: 'Equity', label: 'Equity' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'Expense', label: 'Expense' },
];

/** Layouts: four client-side presentations of STANDARD plus the four server modes. */
type TbLayout =
  | 'STANDARD' | 'SUMMARY' | 'NET' | 'DRCR'
  | 'COMPARATIVE' | 'MONTHLY' | 'DIMENSION' | 'POST_CLOSING';

const LAYOUT_OPTIONS: { value: TbLayout; label: string }[] = [
  { value: 'STANDARD',     label: 'Standard' },
  { value: 'SUMMARY',      label: 'Summary (closing only)' },
  { value: 'NET',          label: 'Net balance' },
  { value: 'DRCR',         label: 'Dr / Cr separate' },
  { value: 'COMPARATIVE',  label: 'Comparative' },
  { value: 'MONTHLY',      label: 'Monthly' },
  { value: 'DIMENSION',    label: 'By dimension' },
  { value: 'POST_CLOSING', label: 'Post-closing' },
];

const GROUP_BY_OPTIONS = [
  { value: '',         label: 'None' },
  { value: 'TYPE',     label: 'Account class' },
  { value: 'CATEGORY', label: 'Account group' },
  { value: 'PARENT',   label: 'Parent account' },
];

const DIMENSION_OPTIONS = [
  { value: 'LOCATION',    label: 'Branch' },
  { value: 'COST_CENTRE', label: 'Cost centre' },
];

/** SUMMARY / NET / DRCR / STANDARD are all presentations of the STANDARD server data. */
function serverMode(layout: TbLayout): TrialBalanceMode {
  if (layout === 'SUMMARY' || layout === 'NET' || layout === 'DRCR' || layout === 'STANDARD') {
    return 'STANDARD';
  }
  return layout;
}

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const TH_CLS = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
const TH_R_CLS = 'px-3 py-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide';
const AMT_CLS = 'px-3 py-2 text-sm text-right tabular-nums';

// ── Column model — drives the table, Excel, CSV, and print for every layout ──

interface TbCol {
  key: string;
  header: string;
  value: (r: TrialBalanceRow) => number | null;
  /** 'amt' — plain amount · 'signed' — net balance (may be negative) · 'pct' — percentage */
  fmt: 'amt' | 'signed' | 'pct';
}

function buildColumns(layout: TbLayout, cellHeaders: string[] | null): TbCol[] {
  switch (layout) {
    case 'SUMMARY':
      return [
        { key: 'closingDr', header: 'Closing Dr', value: r => r.closingDebit, fmt: 'amt' },
        { key: 'closingCr', header: 'Closing Cr', value: r => r.closingCredit, fmt: 'amt' },
      ];
    case 'NET':
      return [
        { key: 'net', header: 'Net Closing Balance', value: r => r.netClosingBalance, fmt: 'signed' },
      ];
    case 'DRCR':
      return [
        { key: 'openingDr', header: 'Opening Dr', value: r => r.openingDebit, fmt: 'amt' },
        { key: 'openingCr', header: 'Opening Cr', value: r => r.openingCredit, fmt: 'amt' },
        { key: 'periodDr',  header: 'Period Dr',  value: r => r.periodDebit, fmt: 'amt' },
        { key: 'periodCr',  header: 'Period Cr',  value: r => r.periodCredit, fmt: 'amt' },
        { key: 'closingDr', header: 'Closing Dr', value: r => r.closingDebit, fmt: 'amt' },
        { key: 'closingCr', header: 'Closing Cr', value: r => r.closingCredit, fmt: 'amt' },
      ];
    case 'COMPARATIVE':
      return [
        { key: 'closing',    header: 'Closing',             value: r => r.netClosingBalance, fmt: 'signed' },
        { key: 'compClose',  header: 'Comparative Closing', value: r => r.comparativeNetClosing, fmt: 'signed' },
        { key: 'diff',       header: 'Difference',          value: r => r.differenceAmount, fmt: 'signed' },
        { key: 'diffPct',    header: 'Diff %',              value: r => r.differencePercent, fmt: 'pct' },
      ];
    case 'MONTHLY':
      return [
        { key: 'opening', header: 'Opening', value: r => r.openingDebit - r.openingCredit, fmt: 'signed' },
        ...(cellHeaders ?? []).map<TbCol>((h, i) => ({
          key: `cell${i}`, header: h, value: r => r.cells?.[i] ?? null, fmt: 'signed',
        })),
        { key: 'ytd',     header: 'YTD Movement', value: sumCells, fmt: 'signed' },
        { key: 'closing', header: 'Closing',      value: r => r.netClosingBalance, fmt: 'signed' },
      ];
    case 'DIMENSION':
      return [
        ...(cellHeaders ?? []).map<TbCol>((h, i) => ({
          key: `cell${i}`, header: h, value: r => r.cells?.[i] ?? null, fmt: 'signed',
        })),
        { key: 'total', header: 'Total', value: sumCells, fmt: 'signed' },
      ];
    case 'POST_CLOSING':
      return [
        { key: 'preClose',  header: 'Pre-Close',  value: r => r.preCloseNet, fmt: 'signed' },
        { key: 'closeDr',   header: 'Closing Dr', value: r => r.closingJournalDebit, fmt: 'amt' },
        { key: 'closeCr',   header: 'Closing Cr', value: r => r.closingJournalCredit, fmt: 'amt' },
        { key: 'postClose', header: 'Post-Close', value: r => r.postCloseNet, fmt: 'signed' },
      ];
    case 'STANDARD':
    default:
      return [
        { key: 'opening',  header: 'Opening',   value: r => r.openingDebit - r.openingCredit, fmt: 'signed' },
        { key: 'periodDr', header: 'Period Dr', value: r => r.periodDebit, fmt: 'amt' },
        { key: 'periodCr', header: 'Period Cr', value: r => r.periodCredit, fmt: 'amt' },
        { key: 'closing',  header: 'Closing',   value: r => r.netClosingBalance, fmt: 'signed' },
      ];
  }
}

function fmtCol(col: TbCol, v: number | null): string {
  if (col.fmt === 'pct') return fmtPct(v);
  return fmtAmt(v);
}

/** Column sums over a set of rows; percent columns are not summable. */
function columnTotals(cols: TbCol[], rows: TrialBalanceRow[]): (number | null)[] {
  return cols.map(col =>
    col.fmt === 'pct' ? null : rows.reduce((s, r) => s + (col.value(r) ?? 0), 0));
}

// ── Direction arrow (COMPARATIVE) ─────────────────────────────────────────────

const DirectionIcon = ({ direction }: { direction: string | null }) => {
  if (direction === 'UP') {
    return <TrendingUp size={13} className="inline text-green-600 dark:text-green-400" aria-label="Up" />;
  }
  if (direction === 'DOWN') {
    return <TrendingDown size={13} className="inline text-red-600 dark:text-red-400" aria-label="Down" />;
  }
  return <Minus size={13} className="inline text-gray-400 dark:text-gray-500" aria-label="Flat" />;
};

// ── Excel / CSV export ────────────────────────────────────────────────────────

function buildExportColumns(cols: TbCol[], grouped: boolean, comparative: boolean): ExcelColumn[] {
  return [
    { key: 'Code', header: 'Code', width: 14 },
    { key: 'Name', header: 'Account Name', width: 36 },
    { key: 'Type', header: 'Type', width: 12 },
    ...(grouped ? [{ key: 'Group', header: 'Group', width: 20 }] : []),
    ...cols.map<ExcelColumn>(c => ({
      key: c.header, header: c.header, width: 15,
      numFmt: c.fmt === 'pct' ? '0.0"%"' : '#,##0.00',
    })),
    ...(comparative ? [{ key: 'Direction', header: 'Direction', width: 10 }] : []),
  ];
}

function buildExportRows(
  data: TrialBalanceDto, cols: TbCol[], grouped: boolean, comparative: boolean,
): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = data.rows.map(r => {
    const rec: Record<string, unknown> = {
      Code: r.accountCode,
      Name: r.accountName,
      Type: r.accountType,
    };
    if (grouped) rec.Group = r.groupKey ?? '';
    for (const c of cols) {
      const v = c.value(r);
      rec[c.header] = v === null ? '' : v;
    }
    if (comparative) rec.Direction = r.movementDirection ?? '';
    return rec;
  });

  const totals = columnTotals(cols, data.rows);
  const totalRec: Record<string, unknown> = { Code: '', Name: 'TOTAL', Type: '' };
  if (grouped) totalRec.Group = '';
  cols.forEach((c, i) => { totalRec[c.header] = totals[i] === null ? '' : totals[i]; });
  if (comparative) totalRec.Direction = '';
  rows.push(totalRec);
  return rows;
}

// ── Component ──────────────────────────────────────────────────────────────────

export const TrialBalancePage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [layout, setLayout] = useState<TbLayout>('DRCR');
  const [filter, setFilter] = useState<TrialBalanceFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    includeZeroBalances: false,
    includeInactive: false,
    includePostingAccountOnly: true,
    dimensionBy: 'LOCATION',
  });
  const [data, setData] = useState<TrialBalanceDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const mode = serverMode(layout);
      const params: TrialBalanceFilter = {
        ...filter,
        mode,
        groupBy: filter.groupBy || undefined,
        comparativeFromDate:     mode === 'COMPARATIVE' ? filter.comparativeFromDate || undefined : undefined,
        comparativeToDate:       mode === 'COMPARATIVE' ? filter.comparativeToDate || undefined : undefined,
        comparativeLocationId:   mode === 'COMPARATIVE' ? filter.comparativeLocationId || undefined : undefined,
        comparativeCostCenterId: mode === 'COMPARATIVE' ? filter.comparativeCostCenterId || undefined : undefined,
        dimensionBy:             mode === 'DIMENSION' ? filter.dimensionBy || 'LOCATION' : undefined,
        financialYearCode:       mode === 'POST_CLOSING' ? filter.financialYearCode || undefined : undefined,
      };
      const res = await reportApi.getTrialBalance(params);
      setData(res.data?.data ?? null);
      setCollapsedGroups(new Set());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter, layout]);

  // Presentation layout in force for the loaded data. Switching between the four
  // STANDARD presentations re-renders instantly; switching to a server mode that
  // does not match the loaded data falls back to that data's own mode until re-run.
  const effectiveLayout: TbLayout = useMemo(() => {
    if (!data) return layout;
    if (serverMode(layout) === data.mode) return layout;
    return (data.mode as TbLayout) ?? 'STANDARD';
  }, [data, layout]);

  const cols = useMemo(
    () => buildColumns(effectiveLayout, data?.cellHeaders ?? null),
    [effectiveLayout, data],
  );

  const isComparative = effectiveLayout === 'COMPARATIVE';
  const isGrouped = !!data?.groupBy;

  // Grouped rows (preserves server ordering within each group)
  const groups = useMemo(() => {
    if (!data || !isGrouped) return null;
    const map = new Map<string, TrialBalanceRow[]>();
    for (const r of data.rows) {
      const k = r.groupKey ?? 'Ungrouped';
      const list = map.get(k);
      if (list) list.push(r);
      else map.set(k, [r]);
    }
    return [...map.entries()];
  }, [data, isGrouped]);

  const totals = useMemo(
    () => (data ? columnTotals(cols, data.rows) : []),
    [data, cols],
  );

  const postClosingNotRun =
    effectiveLayout === 'POST_CLOSING' && data?.postClosingStatus === 'NotRun';

  if (!hasPermission(Permissions.RPT_TRIAL_BALANCE)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const showLocation = !!filter.locationId;
  const showCostCentre = !!filter.costCenterId;
  const leftColCount = 3 + (showLocation ? 1 : 0) + (showCostCentre ? 1 : 0);
  const totalColCount = leftColCount + cols.length + (isComparative ? 1 : 0) + 1; // +Δ dir, +drill

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filterSummary = `${filter.fromDate}..${filter.toDate} · ${effectiveLayout}${data?.groupBy ? ` · grouped by ${data.groupBy}` : ''}`;

  const handleExcel = () => {
    if (!data || postClosingNotRun) return;
    exportToExcel({
      sheetName: 'Trial Balance',
      columns: buildExportColumns(cols, isGrouped, isComparative),
      rows: buildExportRows(data, cols, isGrouped, isComparative),
      title: 'Trial Balance',
      subtitle: `${data.companyName} · ${filter.fromDate} to ${filter.toDate} · ${effectiveLayout}`,
    }, `TrialBalance_${effectiveLayout}_${filter.fromDate}_${filter.toDate}`);
  };

  const handleCsv = () => {
    if (!data || postClosingNotRun) return;
    exportToCsvRows(
      buildExportColumns(cols, isGrouped, isComparative),
      buildExportRows(data, cols, isGrouped, isComparative),
      `TrialBalance_${effectiveLayout}_${filter.fromDate}_${filter.toDate}`,
    );
  };

  const handlePrint = () => setShowPrint(true);

  const applySavedView = (f: unknown) => {
    if (!f || typeof f !== 'object') return;
    const obj = f as Partial<TrialBalanceFilter> & { layout?: TbLayout };
    const { layout: savedLayout, ...rest } = obj;
    if (savedLayout && LAYOUT_OPTIONS.some(o => o.value === savedLayout)) setLayout(savedLayout);
    setFilter(prev => ({ ...prev, ...rest }));
  };

  // ── one data row ────────────────────────────────────────────────────────────
  const renderRow = (row: TrialBalanceRow) => (
    <tr
      key={row.accountId}
      className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 group"
    >
      <td className="px-3 py-2 text-sm font-mono text-gray-600 dark:text-gray-400">{row.accountCode}</td>
      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
        {row.accountName}
        {row.accountCategory && (
          <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({row.accountCategory})</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{row.accountType}</td>
      {showLocation && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">—</td>}
      {showCostCentre && <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">—</td>}
      {cols.map(col => {
        const v = col.value(row);
        return (
          <td
            key={col.key}
            className={cn(
              AMT_CLS,
              col.fmt === 'signed' && v !== null && v < 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-700 dark:text-gray-300',
              (col.key === 'closing' || col.key === 'closingDr' || col.key === 'closingCr' || col.key === 'net' || col.key === 'postClose')
                && 'font-medium text-gray-900 dark:text-gray-100',
            )}
          >
            {fmtCol(col, v)}
          </td>
        );
      })}
      {isComparative && (
        <td className="px-3 py-2 text-center">
          <DirectionIcon direction={row.movementDirection} />
        </td>
      )}
      <td className="px-3 py-2 print:hidden">
        <button
          onClick={() =>
            navigate(
              `${AppRoutes.REPORTS_ACCOUNT_LEDGER}?accountId=${row.accountId}&fromDate=${filter.fromDate}&toDate=${filter.toDate}`
            )
          }
          className="opacity-0 group-hover:opacity-100 text-brand-500 hover:text-brand-700"
          title="View Account Ledger"
        >
          <ExternalLink size={13} />
        </button>
      </td>
    </tr>
  );

  const renderSubtotalRow = (key: string, rows: TrialBalanceRow[]) => {
    const groupSums = columnTotals(cols, rows);
    return (
      <tr key={`${key}-subtotal`} className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40">
        <td colSpan={leftColCount} className="px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 pl-8">
          Subtotal — {key}
        </td>
        {cols.map((col, i) => (
          <td key={col.key} className={cn(AMT_CLS, 'py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200')}>
            {col.fmt === 'pct' ? '—' : fmtAmt(groupSums[i])}
          </td>
        ))}
        {isComparative && <td></td>}
        <td className="print:hidden"></td>
      </tr>
    );
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Trial Balance</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          onPrint={handlePrint}
          hasData={!!data && !postClosingNotRun}
          reportKey="TRIAL_BALANCE"
          rowCount={data?.rows.length ?? 0}
          filterSummary={filterSummary}
        />
      </div>

      {/* Saved views */}
      <SavedViewBar
        reportKey="TRIAL_BALANCE"
        currentFilters={{ ...filter, layout }}
        onApply={applySavedView}
      />

      {/* Filter Panel */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        {/* Row 1 — Layout, Group by, dates */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Layout</label>
            <select
              value={layout}
              onChange={e => setLayout(e.target.value as TbLayout)}
              className={INPUT_CLS}
            >
              {LAYOUT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Group by</label>
            <select
              value={filter.groupBy ?? ''}
              onChange={e => setFilter(f => ({ ...f, groupBy: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {GROUP_BY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
        </div>

        {/* Mode-specific filters */}
        {layout === 'COMPARATIVE' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className={LABEL_CLS}>Compare From Date</label>
              <input
                type="date"
                value={filter.comparativeFromDate ?? ''}
                onChange={e => setFilter(f => ({ ...f, comparativeFromDate: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Compare To Date</label>
              <input
                type="date"
                value={filter.comparativeToDate ?? ''}
                onChange={e => setFilter(f => ({ ...f, comparativeToDate: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Compare Against Location</label>
              <LocationSelect
                value={filter.comparativeLocationId}
                onChange={id => setFilter(f => ({ ...f, comparativeLocationId: id ?? undefined }))}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Compare Against Cost Centre</label>
              <CostCentreSelect
                value={filter.comparativeCostCenterId}
                onChange={id => setFilter(f => ({ ...f, comparativeCostCenterId: id ?? undefined }))}
              />
            </div>
          </div>
        )}
        {layout === 'DIMENSION' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLS}>Dimension</label>
              <select
                value={filter.dimensionBy ?? 'LOCATION'}
                onChange={e => setFilter(f => ({ ...f, dimensionBy: e.target.value }))}
                className={INPUT_CLS}
              >
                {DIMENSION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {layout === 'POST_CLOSING' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={LABEL_CLS}>Financial Year *</label>
              <input
                type="text"
                value={filter.financialYearCode ?? ''}
                placeholder="e.g. FY2026"
                onChange={e => setFilter(f => ({ ...f, financialYearCode: e.target.value || undefined }))}
                className={INPUT_CLS}
              />
            </div>
          </div>
        )}

        {/* Row 2 — Quick Period */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Quick Period</label>
            <FinancialPeriodSelect
              onPeriodChange={(from, to) => {
                setFilter(f => ({ ...f, fromDate: from, toDate: to }));
              }}
            />
          </div>
        </div>

        {/* Row 3 — Location, Cost Centre, Account Type */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id ?? undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Type</label>
            <select
              value={filter.accountTypeFilter ?? ''}
              onChange={e => setFilter(f => ({ ...f, accountTypeFilter: e.target.value || undefined }))}
              className={INPUT_CLS}
            >
              {ACCOUNT_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 4 — Account Code range */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Account Code From</label>
            <input
              type="text"
              value={filter.accountCodeFrom ?? ''}
              placeholder="e.g. 1000"
              onChange={e => setFilter(f => ({ ...f, accountCodeFrom: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Code To</label>
            <input
              type="text"
              value={filter.accountCodeTo ?? ''}
              placeholder="e.g. 9999"
              onChange={e => setFilter(f => ({ ...f, accountCodeTo: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
        </div>

        {/* Checkboxes + Run button */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.includeZeroBalances ?? false}
                onChange={e => setFilter(f => ({ ...f, includeZeroBalances: e.target.checked }))}
                className="rounded"
              />
              Include Zero Balances
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.includeInactive ?? false}
                onChange={e => setFilter(f => ({ ...f, includeInactive: e.target.checked }))}
                className="rounded"
              />
              Include Inactive
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.includePostingAccountOnly ?? true}
                onChange={e => setFilter(f => ({ ...f, includePostingAccountOnly: e.target.checked }))}
                className="rounded"
              />
              Posting Accounts Only
            </label>
          </div>
          <RunReportButton
            onClick={run}
            isLoading={loading}
            disabled={
              !filter.fromDate || !filter.toDate ||
              (layout === 'POST_CLOSING' && !filter.financialYearCode)
            }
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading (no data yet) */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Results */}
      {data && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col min-h-0 flex-1">
          {/* Report header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Trial Balance — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                {' · '}{LAYOUT_OPTIONS.find(o => o.value === effectiveLayout)?.label ?? effectiveLayout}
                {isComparative && data.comparativeFromDate && (
                  <> {' · '}vs {fmtDate(data.comparativeFromDate)} to {fmtDate(data.comparativeToDate)}</>
                )}
                {' · '}{data.rows.length} accounts
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>
            <div className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
              data.isBalanced
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
            )}>
              {data.isBalanced ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
              {data.isBalanced ? 'Balanced' : 'OUT OF BALANCE'}
            </div>
          </div>

          {/* Post-closing not-run banner */}
          {postClosingNotRun ? (
            <div className="m-4 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-300">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">No completed year-end close found for this financial year</div>
                <div className="text-xs mt-1">
                  Post-closing balances become available once the year-end close for{' '}
                  {filter.financialYearCode ?? 'the selected year'} has completed.
                </div>
              </div>
            </div>
          ) : (
            /* Print area */
            <div data-print-section id="tb-print" className="overflow-auto flex-1">
              {/* Print-only header */}
              <div className="hidden print:block px-4 pt-4 pb-2 text-center">
                <div className="text-lg font-bold">{data.companyName}</div>
                <div className="text-base font-semibold mt-1">Trial Balance</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                  {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                </div>
              </div>

              <table className={cn('w-full text-sm border-collapse', cols.length > 8 ? 'min-w-[1400px]' : 'min-w-[900px]')}>
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-28')}>Code</th>
                    <th className={TH_CLS}>Account Name</th>
                    <th className={cn(TH_CLS, 'w-20')}>Type</th>
                    {showLocation && <th className={cn(TH_CLS, 'w-24')}>Location</th>}
                    {showCostCentre && <th className={cn(TH_CLS, 'w-24')}>Cost Centre</th>}
                    {cols.map(col => (
                      <th key={col.key} className={cn(TH_R_CLS, 'w-28 whitespace-nowrap')}>{col.header}</th>
                    ))}
                    {isComparative && <th className={cn(TH_CLS, 'w-10 text-center')}>Δ</th>}
                    <th className="px-3 py-2 w-8 print:hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={totalColCount}
                        className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600"
                      >
                        No accounts match the selected criteria.
                      </td>
                    </tr>
                  ) : groups ? (
                    groups.map(([key, rows]) => {
                      const isCollapsed = collapsedGroups.has(key);
                      return (
                        <FragmentRows
                          key={key}
                          groupKey={key}
                          rows={rows}
                          collapsed={isCollapsed}
                          onToggle={() => toggleGroup(key)}
                          totalColCount={totalColCount}
                          renderRow={renderRow}
                          renderSubtotal={renderSubtotalRow}
                        />
                      );
                    })
                  ) : (
                    data.rows.map(renderRow)
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50">
                    <td
                      colSpan={leftColCount}
                      className="px-3 py-2 text-sm font-bold text-gray-900 dark:text-white"
                    >
                      TOTAL
                    </td>
                    {cols.map((col, i) => (
                      <td key={col.key} className={cn(AMT_CLS, 'font-bold text-gray-900 dark:text-white')}>
                        {col.fmt === 'pct' ? '—' : fmtAmt(totals[i])}
                      </td>
                    ))}
                    {isComparative && <td></td>}
                    <td className="print:hidden"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {!data && !loading && (
        <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm">
          Set the date range and click "Run Report" to generate the Trial Balance.
        </div>
      )}

      {/* ── Print Preview ─────────────────────────────────────────────────── */}
      {data && !postClosingNotRun && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Trial Balance"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Trial Balance"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period',  value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Layout',  value: LAYOUT_OPTIONS.find(o => o.value === effectiveLayout)?.label ?? effectiveLayout },
              { label: 'Status',  value: data.isBalanced ? 'Balanced ✓' : 'OUT OF BALANCE ⚠' },
              { label: 'Accounts', value: String(data.rows.length) },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Code', 'Account Name', 'Type', ...cols.map(c => c.header), ...(isComparative ? ['Δ'] : [])].map((h, i) => (
                  <th key={`${h}-${i}`} style={{ background: '#1e3a5f', color: 'white', padding: '5px 6px', textAlign: i >= 3 ? 'right' : 'left', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row: TrialBalanceRow) => (
                <tr key={row.accountId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#6b7280' }}>{row.accountCode}</td>
                  <td style={{ padding: '3px 6px' }}>{row.accountName}</td>
                  <td style={{ padding: '3px 6px', color: '#6b7280', fontSize: '8px' }}>{row.accountType}</td>
                  {cols.map(col => {
                    const v = col.value(row);
                    return (
                      <td key={col.key} style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', color: col.fmt === 'signed' && v !== null && v < 0 ? '#dc2626' : undefined }}>
                        {fmtCol(col, v)}
                      </td>
                    );
                  })}
                  {isComparative && (
                    <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                      {row.movementDirection === 'UP' ? '▲' : row.movementDirection === 'DOWN' ? '▼' : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid #1e3a5f', background: '#f9fafb' }}>
                <td colSpan={3} style={{ padding: '4px 6px', fontWeight: 700 }}>TOTAL</td>
                {cols.map((col, i) => (
                  <td key={col.key} style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                    {col.fmt === 'pct' ? '—' : fmtAmt(totals[i])}
                  </td>
                ))}
                {isComparative && <td></td>}
              </tr>
            </tfoot>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};

// ── Group block (header + rows + subtotal) ────────────────────────────────────

interface FragmentRowsProps {
  groupKey: string;
  rows: TrialBalanceRow[];
  collapsed: boolean;
  onToggle: () => void;
  totalColCount: number;
  renderRow: (row: TrialBalanceRow) => React.ReactNode;
  renderSubtotal: (key: string, rows: TrialBalanceRow[]) => React.ReactNode;
}

const FragmentRows = ({
  groupKey, rows, collapsed, onToggle, totalColCount, renderRow, renderSubtotal,
}: FragmentRowsProps) => (
  <>
    <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-100/80 dark:bg-gray-800/70">
      <td colSpan={totalColCount} className="px-3 py-1.5">
        <button
          onClick={onToggle}
          className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wide"
          title={collapsed ? 'Expand group' : 'Collapse group'}
        >
          {collapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
          {groupKey}
          <span className="font-normal normal-case text-gray-400 dark:text-gray-500">
            ({rows.length} {rows.length === 1 ? 'account' : 'accounts'})
          </span>
        </button>
      </td>
    </tr>
    {!collapsed && rows.map(renderRow)}
    {renderSubtotal(groupKey, rows)}
  </>
);
